import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useFetchAccount } from '@/features/account/api/get-account';
import useFetchAccountV2 from '@/features/account/api/get-account-v2';
import useBalance from '@/features/account/api/get-balance';
import useOpenOrders from '@/features/account/api/get-open-orders';
import {
  useLegacyOpenOrders,
  useOrders,
} from '@/features/account/api/get-orders';
import useOpenPositions from '@/features/trade/order/api/get-opened-positions';
import { loadLeverages } from '@/lib/leverage-db';
import { useAccountStore } from '@/store/use-account-store';
import { resetAllLeverages, setAllLeverages } from '@/store/use-markets-store';
import {
  cloneDefaultWalletScopedOrders,
  useOrdersStore,
} from '@/store/use-orders-store';
import {
  handleOrderForStore,
  orderResponseToOrder,
} from '@/utils/handle-store-data';

export default function AccountHandler() {
  const {
    data: openOrders,
    isPending: areOrdersPending,
    isRefetching: areOrdersRefetching,
  } = useLegacyOpenOrders();

  useFetchAccount();
  useFetchAccountV2();
  useOpenPositions();
  useBalance();
  useOpenOrders();

  useEffect(() => {
    if (!openOrders) return;
    const newOrderIds: Set<string> = new Set();
    openOrders.forEach((res) => {
      const order = orderResponseToOrder(res);
      handleOrderForStore(order, { skipFill: true });
      newOrderIds.add(res.id);
    });
    const { address } = useAccountStore.getState();
    if (!address) return;
    // Remove existing store new orders that are no longer open
    useOrdersStore.setState((state) => ({
      byWallet: {
        ...state.byWallet,
        [address]: {
          ...cloneDefaultWalletScopedOrders(),
          ...(state.byWallet[address] ?? {}),
          newOrderIds,
        },
      },
    }));
  }, [openOrders]);

  const { address, accGroup } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      accGroup: state.getAuthParams(state.address)?.accountGroup,
    })),
  );

  useEffect(() => {
    if (accGroup !== undefined && accGroup !== 2) return;

    resetAllLeverages();

    if (!address) return;

    loadLeverages(address).then((leverages) => {
      if (Object.keys(leverages).length > 0) {
        setAllLeverages(leverages);
      }
    });
  }, [address, accGroup]);

  const refetchInterval = useOrdersStore((state) => state.refetchInterval);
  const openOrdersIds = useOrdersStore(
    useShallow((state) => Array.from(state.getOrders(address).newOrderIds)),
  );

  useOrders({
    id: openOrdersIds,
    onSuccess: (res) =>
      res.forEach((data) => handleOrderForStore(orderResponseToOrder(data))),
    queryConfig: {
      enabled:
        accGroup !== undefined &&
        accGroup !== 2 &&
        !areOrdersPending &&
        !areOrdersRefetching &&
        openOrdersIds.length > 0,
      refetchInterval,
      refetchOnMount: false,
    },
  });

  return <></>;
}
